// === FILE HEADER: SPA Logger Utility ===
// This file consolidates all logger utilities for the Chess Slider SPA.
// Includes DOM event logging, function call logging, and logger configuration.
// Structure and section anchors follow project documentation and copilot-instructions.md.
// Last updated: 2025-07-20
//
// === END FILE HEADER ===
// === COPILOT FILE RULES ===
// DO NOT DELETE THE HEADER OR FILE RULES FROM THE FILE
// DO NOT PLACE ANY CODE, HOOKS, OR LOGIC ABOVE IMPORTS. 
// All import statements must be at the very top of the file, before any other code or comments (except for a file-level comment header or license block, if present).
// React hooks (useEffect, useState, etc.) must only be called inside a React function component or custom hook, never at the top level.
// Make a copy of the file in copies folder before doing any edits.
// Updating full large files has been difficult.  You may use the copy to refer to if it helps you.
// After completing edits compare the edited file to the original and make sure nothing other than the intended changes have happened
// If there are unintended changes repair the file
// After user approves the copy may be deleted
// DO NOT USE INLINE STYLES ALL STYLES MUST BE DEFINED IN THE PROPER FILE AND IMPORTED
// === END COPILOT FILE RULES ===

// === Logger Constants ===
const MAX_LOG_OUTPUT_LINES = 200;
const MAX_ATTR_LENGTH = 60;
const MAX_CLASSNAME_LENGTH = 80;
const MAX_ATTRIBUTES = 10;

// === Logger Port Mapping Utility (formerly loggerConfig.js) ===
const LOGGER_PORTS = {
  Functions: 5001,
  Board: 5002,
  GameControls: 5003,
  MoveHistoryDialog: 5005,
  PowerUpControls: 5006,
  SettingsPanel: 5007,
  Square: 5009,
  StartupDialog: 5010,
  Tile: 5011,
  App: 5012,
  VictoryDialog: 5013,
  TileLayer: 5014,
};

export function getLoggerPort(componentName) {
  return LOGGER_PORTS[componentName] || null;
}
export { LOGGER_PORTS };

// === Logger Toggles Loaded Global Flag ===
let loggerTogglesLoaded = false;
export function setLoggerTogglesLoaded(val) {
  loggerTogglesLoaded = !!val;
}

export function isLoggerPortEnabled(componentName) {
  if (!loggerTogglesLoaded) return false;
  const loggerState = (typeof window !== 'undefined' && window.__SPA_LOGGER_STATE__)
    ? window.__SPA_LOGGER_STATE__
    : {
        spaLogger: false,
        consoleLogger: false,
        ports: {
          Functions: false,
          Board: false,
          GameControls: false,
          MoveHistoryDialog: false,
          SettingsPanel: false,
          Square: false,
          StartupDialog: false,
          Tile: false,
          App: false,
          VictoryDialog: false,
          TileLayer: false
        }
      };
  return !!loggerState.ports[componentName];
}

// === Logger State Helper ===
function getLoggerState() {
  if (!loggerTogglesLoaded) return { spaLogger: false, consoleLogger: false, ports: {} };
  if (typeof window !== 'undefined' && window.__SPA_LOGGER_STATE__) {
    return window.__SPA_LOGGER_STATE__;
  }
  // Default: all OFF
  return {
    spaLogger: false,
    consoleLogger: false,
    ports: {
      Functions: false,
      Board: false,
      GameControls: false,
      MoveHistoryDialog: false,
      SettingsPanel: false,
      Square: false,
      StartupDialog: false,
      Tile: false,
      App: false,
      VictoryDialog: false,
      TileLayer: false
    }
  };
}

// === Section: DOM Event Logger Utility ===
/**
 * Send a DOM event log to the SPA logger API.
 * @param {string} componentName - The name of the component (e.g., 'Board', 'Tile', etc.)
 * @param {string} eventType - The type of event (e.g., 'click', 'hover', 'tooltip').
 * @param {string} element - Class name (preferred) or tag name of the element.
 * @param {string} html - The HTML string of the relevant DOM subtree.
 * @param {HTMLElement} dom - The DOM element to extract UI info from (optional, but recommended).
 * @param {string} [parent] - Class name (preferred) or tag name of the parent element (optional).
 */
export async function logDomEvent(componentName, eventType, element, html, dom, parent) {
  if (!loggerTogglesLoaded) {
    // eslint-disable-next-line no-console
    console.log('[SPA LOG DOM] loggerTogglesLoaded is false, skipping logDomEvent');
    return;
  }
  const loggerState = getLoggerState();
  // eslint-disable-next-line no-console
  console.log('[SPA LOG DOM] loggerState:', loggerState);
  const portEnabled = !!loggerState.ports[componentName];
  const spaLoggerOn = !!loggerState.spaLogger;
  const consoleLoggerOn = !!loggerState.consoleLogger;
  if (!portEnabled) {
    // eslint-disable-next-line no-console
    console.log(`[SPA LOG DOM] Logger port for component ${componentName} is not enabled.`);
    return;
  }
  if (!spaLoggerOn && !consoleLoggerOn) {
    // eslint-disable-next-line no-console
    console.log('[SPA LOG DOM] Both spaLogger and consoleLogger are off, skipping logDomEvent');
    return;
  }

  let domInfo = undefined;
  let computedStyles = undefined;
  let elementName = element;
  let parentName = parent;
  if (!parentName) {
    if (dom && dom.parentElement) {
      parentName = dom.parentElement.className ? dom.parentElement.className : dom.parentElement.tagName;
    } else {
      parentName = 'unknown';
    }
  }
  if (dom && dom.getBoundingClientRect) {
    const rect = dom.getBoundingClientRect();
    const computed = window.getComputedStyle(dom);
    const averageList = [
      'color', 'background', 'background-color',
      'border', 'border-radius', 'border-color', 'border-style', 'border-width',
      'box-shadow', 'box-sizing', 'display', 'position',
      'width', 'height', 'min-width', 'min-height', 'max-width', 'max-height',
      'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
      'padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right',
      'font-family', 'font-size', 'font-weight', 'font-style', 'line-height', 'text-align', 'text-shadow', 'letter-spacing',
      'align-items', 'justify-content', 'flex-direction', 'flex', 'flex-basis', 'flex-grow', 'flex-shrink', 'flex-wrap',
      'z-index', 'cursor', 'transition', 'transition-property', 'transition-duration', 'transition-timing-function',
      'overflow', 'overflow-x', 'overflow-y', 'opacity', 'object-fit', 'user-select'
    ];
    const appliedStyles = {};
    for (let i = 0; i < computed.length; i++) {
      const prop = computed[i];
      if (prop.startsWith('--') || averageList.includes(prop)) {
        appliedStyles[prop] = computed.getPropertyValue(prop);
      }
    }
    // Get className or tagName for element
    elementName = dom.className ? (typeof dom.className === 'string' && dom.className.length > MAX_CLASSNAME_LENGTH ? dom.className.slice(0, MAX_CLASSNAME_LENGTH - 3) + '...' : dom.className) : dom.tagName;
    // Extract attributes (excluding class, id, style), limit to MAX_ATTRIBUTES, truncate long values
    const attributes = {};
    if (dom.attributes) {
      let count = 0;
      for (let attr of dom.attributes) {
        if (attr.name !== 'class' && attr.name !== 'id' && attr.name !== 'style') {
          if (typeof attr.value === 'string' || typeof attr.value === 'number') {
            let val = attr.value;
            if (typeof val === 'string' && val.length > MAX_ATTR_LENGTH) val = val.slice(0, MAX_ATTR_LENGTH - 3) + '...';
            attributes[attr.name] = val;
            count++;
            if (count >= MAX_ATTRIBUTES) break;
          }
        }
      }
    }
    domInfo = {
      tagName: typeof dom.tagName === 'string' ? dom.tagName : '',
      className: typeof dom.className === 'string' ? (dom.className.length > MAX_CLASSNAME_LENGTH ? dom.className.slice(0, MAX_CLASSNAME_LENGTH - 3) + '...' : dom.className) : '',
      id: typeof dom.id === 'string' ? dom.id : '',
      attributes,
      appliedStyles,
      boundingRect: {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left
      }
    };
    computedStyles = appliedStyles;
  }
  const payload = {
    timestamp: new Date().toISOString(),
    componentName,
    element: elementName,
    parent: parentName,
    eventType,
    html,
    ...(domInfo ? { dom: domInfo } : {}),
    ...(computedStyles ? { computedStyles } : {})
  };
  function safeStringify(obj) {
    const seen = new WeakSet();
    const json = JSON.stringify(obj, function(key, value) {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) return undefined;
        seen.add(value);
      }
      if (typeof value === 'function') return undefined;
      return value;
    }, 2);
    const arr = json.split('\n');
    if (arr.length > MAX_LOG_OUTPUT_LINES) {
      return arr.slice(0, MAX_LOG_OUTPUT_LINES).join('\n') + '\n// Output truncated to ' + MAX_LOG_OUTPUT_LINES + ' lines for readability.';
    }
    return json;
  }
  if (spaLoggerOn) {
    const port = getLoggerPort(componentName);
    // eslint-disable-next-line no-console
    console.log(`[SPA LOG DOM] Using port: ${port} for component: ${componentName}`);
    // === Logger Host Override Support ===
    let loggerHost = 'localhost';
    try {
      const saved = localStorage.getItem('spaLoggerState');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.loggerHostOverride && parsed.loggerHost) {
          loggerHost = parsed.loggerHost;
          // eslint-disable-next-line no-console
          console.log(`[SPA LOG DOM] Logger host override found: ${loggerHost}`);
        } else {
          // eslint-disable-next-line no-console
          console.log('[SPA LOG DOM] No logger host override, using default localhost');
        }
      } else {
        // eslint-disable-next-line no-console
        console.log('[SPA LOG DOM] No spaLoggerState found in localStorage');
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SPA LOG DOM] Error reading spaLoggerState from localStorage:', e);
    }
    const LOGGER_URL = port ? `http://${loggerHost}:${port}/logs` : null;
    // eslint-disable-next-line no-console
    console.log(`[SPA LOG DOM] LOGGER_URL: ${LOGGER_URL}`);
    if (LOGGER_URL) {
      try {
        // eslint-disable-next-line no-console
        console.log('[SPA LOG DOM] Sending payload:', safeStringify(payload));
        await fetch(LOGGER_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: safeStringify(payload),
        });
        // eslint-disable-next-line no-console
        console.log('[SPA LOG DOM] Payload sent successfully');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[SPA LOG DOM] Failed to log DOM event:', err);
      }
    } else {
      // eslint-disable-next-line no-console
      console.error(`[SPA LOG DOM] Logger port not found for component: ${componentName}`);
    }
  }
  if (consoleLoggerOn) {
    // eslint-disable-next-line no-console
    console.log('[SPA LOG DOM] Console logger output:', componentName, safeStringify(payload));
  }
}

// === Section: Function Call Logger Utility ===
/**
 * Send a function call log to the SPA logger API.
 * @param {string} componentName - The name of the component making the call.
 * @param {string} functionName - The name of the function being called.
 * @param {object|array} params - The parameters passed to the function.
 */
export async function logFunctionCall(componentName, functionName, params) {
  try {
    const loggerState = (typeof window !== 'undefined' && window.__SPA_LOGGER_STATE__)
      ? window.__SPA_LOGGER_STATE__
      : { spaLogger: false, consoleLogger: false, ports: {} };
    if (!loggerState.spaLogger || !loggerState.ports.Functions) {
      return;
    }
  } catch (e) {
    return;
  }
  const payload = {
    eventType: 'function_call',
    functionName,
    params,
    componentName,
    timestamp: new Date().toISOString(),
  };
  // === Logger Host Override Support ===
  let loggerHost = 'localhost';
  try {
    const saved = localStorage.getItem('spaLoggerState');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.loggerHostOverride && parsed.loggerHost) {
        loggerHost = parsed.loggerHost;
      }
    }
  } catch (e) { /* ignore */ }
  try {
    await fetch(`http://${loggerHost}:${LOGGER_PORTS.Functions}/logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    // Suppress error output to avoid console spam when logger backend is down
  }
}

// === End spaLogger.js ===
